import {
  Component,
  ElementRef,
  HostListener,
  inject,
  OnInit,
  signal,
  Signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { FieldType, FieldTypeConfig, FormlyModule } from '@ngx-formly/core';
import { LookupList } from '../../../../../core/models/lookup-list.model';
import { LookupListService } from '../../../../../core/services/lookup-list.service';
import { IconComponent } from '../../../../../shared/components/icon/icon.component';
import { TranslatePipe } from '../../../../../shared/pipes/translate.pipe';

/**
 * Formly field type for a custom dropdown (single-select).
 *
 * Renders a styled trigger button instead of a native <select>.
 * Clicking the trigger opens a compact option panel; selecting an option
 * closes it and writes the value to the form control.
 *
 * Props:
 * - label: string            — field label
 * - description: string      — helper text
 * - required: boolean        — whether a selection is mandatory
 * - placeholder: string      — text shown when no option is selected
 * - options: Array<{value, label, description?, isDefault?}> — option list
 */
@Component({
  selector: 'app-dropdown-field',
  imports: [ReactiveFormsModule, FormlyModule, IconComponent, TranslatePipe],
  templateUrl: './dropdown-field.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './dropdown-field.component.css',
})
export class DropdownFieldComponent extends FieldType<FieldTypeConfig> implements OnInit {
  private lookupListService = inject(LookupListService);
  private _lookupListSignal: Signal<LookupList | null> | null = null;
  isOpen = signal(false);

  ngOnInit(): void {
    const optionsSource = this.props['optionsSource'] as string | undefined;
    const lookupListId = this.props['lookupListId'] as string | undefined;
    if (optionsSource === 'lookup-list' && lookupListId) {
      const query = this.lookupListService.createQueryById(lookupListId);
      this._lookupListSignal = query.data;
    }
    // Apply default value when the control has no value yet (new form interaction)
    if (this.formControl.value === null || this.formControl.value === undefined) {
      const defaultOpt = this.dropdownOptions.find((o) => o.isDefault);
      if (defaultOpt) {
        this.formControl.setValue(defaultOpt.value);
      }
    }
  }

  get dropdownOptions(): {
    value: any;
    label: string;
    description?: string;
    isDefault?: boolean;
  }[] {
    if (this._lookupListSignal) {
      return this._lookupListSignal()?.options ?? [];
    }
    const opts = this.props['options'] as any;
    return Array.isArray(opts) ? opts : [];
  }

  get selectedLabel(): string | null {
    const val = this.formControl.value;
    if (val === null || val === undefined) return null;
    return this.dropdownOptions.find((o) => o.value === val)?.label ?? null;
  }

  get placeholder(): string {
    return this.props['placeholder'] ?? 'Select…';
  }

  isSelected(value: any): boolean {
    return this.formControl.value === value;
  }

  toggleOpen(): void {
    this.isOpen.update((v) => !v);
  }

  selectOption(value: any): void {
    this.formControl.setValue(value);
    this.formControl.markAsTouched();
    this.isOpen.set(false);
  }

  /** Close the panel when the user clicks anywhere outside this component */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this._el.nativeElement.contains(event.target)) {
      this.isOpen.set(false);
    }
  }

  constructor(private _el: ElementRef) {
    super();
  }
}
